// Tela do Pós-venda na Visão Geral (#6503): Dhaiane. Abre no dia, sem seletor.
//
// Pergunta da tela: quais análises e clientes novos precisam de mim hoje?
//   - análises cadastrais para decidir, com o prazo de 2 dias úteis por rodada, e as que
//     esperam documento ou garantia do cliente (regra em src/lib/visaoGeral/posVenda.ts);
//   - régua de cobrança da carteira e indicações (os mesmos blocos da Cobrança);
//   - no mês: análises no prazo (meta 90%), análises do mês e a inadimplência da empresa;
//   - detalhe: por que as análises reprovam (motivo da #6428).
// Acordos cumpridos (#6506): parcelas dos acordos que ela fechou pagas até o vencimento.
import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardCheck, Gift, Send, Workflow } from 'lucide-react';
import { hojeIsoLocal } from '../../../src/lib/conferenciaFaturamentoDb';
import { formatarValorKpi } from '../../../src/lib/dashboard/kpisDepartamento';
import { useMotivosReprovacao } from '../../../src/hooks/useMotivosReprovacao';
import { rotuloMotivoReprovacaoAnalise } from '../../../src/lib/motivosReprovacao';
import { reguaDaCarteira } from '../../../src/lib/visaoGeral/cobranca';
import { META_ANALISES_NO_PRAZO, analisesDoMes, filaDaAnalise } from '../../../src/lib/visaoGeral/posVenda';
import { BlocosDoDia } from '../BlocosDoDia';
import { CartaoIndicacoes } from '../CartaoIndicacoes';
import { CartoesReguaEAcordos } from '../blocosFase2';
import { PainelReguaDaCarteira } from '../PainelReguaDaCarteira';
import { BotaoAcao, CabecalhoVisaoGeral, CartaoHoje, CartaoIndicador, ListaAtalhos, PainelDetalhe, SecaoVisaoGeral, plural } from '../componentes';
import { useReguaDaCarteira } from '../useCobranca';
import { useInadimplenciaDaEmpresa } from '../useInadimplenciaDaEmpresa';
import { useDecisoesDoPeriodo, useFilaDaAnalise, useReprovacoesDoPeriodo } from '../usePosVenda';
import { ListaMotivos } from './TelaComercial';
import type { PropsTelaVisaoGeral } from '../tipos';

const LISTA = 8;
const MESES = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];
const reais = (v: number) => formatarValorKpi(v, 'reais');
const kwh = (v: number | string | null) => (Number(v) > 0 ? formatarValorKpi(Number(v), 'kwh') : null);
const pct = (v: number) => `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const dataCurta = (iso: string | null | undefined) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}` : '—');

const TelaPosVenda: React.FC<PropsTelaVisaoGeral> = ({ pessoa, souEu, definicao, periodo }) => {
  const navigate = useNavigate();
  const hoje = hojeIsoLocal();
  const { inicioMes, fimMes, rotuloMes, primeiroDia, ultimoDia } = useMemo(() => {
    const h = new Date();
    const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return {
      inicioMes: new Date(h.getFullYear(), h.getMonth(), 1).toISOString(),
      fimMes: new Date(h.getFullYear(), h.getMonth() + 1, 1).toISOString(),
      rotuloMes: `${MESES[h.getMonth()]}/${h.getFullYear()}`,
      primeiroDia: iso(new Date(h.getFullYear(), h.getMonth(), 1)),
      ultimoDia: iso(new Date(h.getFullYear(), h.getMonth() + 1, 0)),
    };
  }, []);

  // ── dados ──
  const fila = useFilaDaAnalise();
  const decisoes = useDecisoesDoPeriodo(inicioMes, fimMes);
  const reprovacoes = useReprovacoesDoPeriodo(inicioMes, fimMes);
  const acoes = useReguaDaCarteira(pessoa.id);
  const { inadimplencia, meta: metaInad } = useInadimplenciaDaEmpresa();
  const { motivos: listaMotivos } = useMotivosReprovacao();

  // ── contas ──
  const analise = useMemo(() => (fila.dados ? filaDaAnalise(fila.dados.negociacoes, fila.dados.registros, hoje) : null), [fila.dados, hoje]);
  const doMes = useMemo(
    () => (decisoes.dados ? analisesDoMes(decisoes.dados.registros, decisoes.dados.assinaturas, pessoa.id, inicioMes, fimMes) : null),
    [decisoes.dados, pessoa.id, inicioMes, fimMes],
  );
  const regua = useMemo(() => (acoes.dados ? reguaDaCarteira(acoes.dados, pessoa.id, hoje) : null), [acoes.dados, pessoa.id, hoje]);
  const documentos = analise?.esperandoCliente.filter(e => e.motivo === 'documento').length ?? 0;
  const maisAntigaCliente = analise?.esperandoCliente[0] ?? null;
  const abrir = (id: number) => navigate(`/crm?negociacaoId=${id}`);

  const acoesRapidas = souEu ? <BotaoAcao rotulo="Validação de dados" icone={ClipboardCheck} para="/gestao" /> : null;

  return (
    <div className="space-y-8">
      <CabecalhoVisaoGeral nome={pessoa.full_name} pergunta={definicao.pergunta} periodo={periodo} acoes={acoesRapidas} />

      <BlocosDoDia pessoa={pessoa} perfil="pos-venda" />

      <SecaoVisaoGeral titulo="Hoje" subtitulo="análises, clientes e carteira">
        <CartaoHoje
          valor={analise ? analise.paraDecidir.length : null}
          rotulo="análises cadastrais para decidir"
          tom={analise && analise.atrasadas > 0 ? 'critico' : 'atencao'}
          para="/gestao"
          dica={analise ? `${plural(analise.atrasadas, 'já passou', 'já passaram')} do prazo de 2 dias úteis. O prazo conta da assinatura do termo e vale só para a primeira análise: reanálise (voltou para a fila) não tem prazo.` : undefined}
        />
        <CartaoHoje
          valor={analise ? analise.esperandoCliente.length : null}
          rotulo="análises esperando o cliente"
          tom="neutro"
          para="/gestao"
          dica={analise ? `${documentos} esperando documento e ${analise.esperandoCliente.length - documentos} esperando garantia.${maisAntigaCliente?.dias != null ? ` A mais antiga espera há ${plural(maisAntigaCliente.dias, 'dia', 'dias')}.` : ''} Essa espera não conta no prazo da análise.` : undefined}
        />
        <CartaoHoje
          valor={regua ? regua.hoje.length + regua.atrasadas.length : null}
          rotulo="ações da régua (hoje e atrasadas)"
          tom={regua && regua.atrasadas.length > 0 ? 'critico' : 'atencao'}
          para="/financeiro/lembretes-cobranca"
          dica={regua ? `${regua.hoje.length} para hoje e ${regua.atrasadas.length} atrasadas${regua.maisAntiga ? `, a mais antiga de ${dataCurta(regua.maisAntiga)}` : ''}, na sua carteira de cobrança.` : undefined}
        />
        <CartaoIndicacoes pessoa={pessoa} />
      </SecaoVisaoGeral>
      {(fila.erro || acoes.erro) && <p className="-mt-6 text-xs text-red-600 dark:text-red-400">Não foi possível carregar parte da fila.</p>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <PainelDetalhe titulo="Análises para decidir" acao={{ rotulo: 'Validação de dados', para: '/gestao' }}>
          {!analise ? (
            <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
          ) : analise.paraDecidir.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhum termo assinado esperando a análise.</p>
          ) : (
            <>
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {analise.paraDecidir.slice(0, LISTA).map(p => (
                  <li key={p.negociacao.id}>
                    <button type="button" onClick={() => abrir(p.negociacao.id)}
                      className="w-full flex items-start gap-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg px-2 -mx-2">
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{p.negociacao.cliente_nome || 'Sem nome'}</span>
                        <span className="block text-xs text-gray-400">
                          {p.reanalise ? 'reanálise, voltou para a fila' : 'termo assinado'} em {dataCurta(p.inicio)}
                          {kwh(p.negociacao.consumo_medio_kwh) ? ` · ${kwh(p.negociacao.consumo_medio_kwh)}` : ''}
                        </span>
                      </span>
                      <span className={`shrink-0 text-right text-xs font-semibold ${p.atrasada ? 'text-red-600 dark:text-red-400' : p.prazo === hoje ? 'text-amber-700 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400'}`}>
                        {p.reanalise ? 'sem prazo' : p.atrasada ? 'passou do prazo' : p.prazo === hoje ? 'prazo hoje' : `prazo ${dataCurta(p.prazo)}`}
                        <span className="block font-normal text-gray-400">{p.atrasada ? `era ${dataCurta(p.prazo)}` : `${plural(p.diasUteis ?? 0, 'dia útil', 'dias úteis')} na fila`}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-400 mt-3">
                Prazo de 2 dias úteis para a primeira análise (feriados contam como dia não útil), o mais próximo primeiro. Reanálise não tem prazo e fica no fim.
                {analise.paraDecidir.length > LISTA && ` Mais ${plural(analise.paraDecidir.length - LISTA, 'análise', 'análises')} na Validação de Dados.`}
              </p>
            </>
          )}
        </PainelDetalhe>

        <PainelDetalhe titulo="Esperando o cliente" acao={{ rotulo: 'Validação de dados', para: '/gestao' }}>
          {!analise ? (
            <div className="h-24 rounded-lg bg-gray-100 dark:bg-gray-700 animate-pulse" />
          ) : analise.esperandoCliente.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Nenhuma análise parada esperando documento ou garantia.</p>
          ) : (
            <>
              <ul className="divide-y divide-gray-100 dark:divide-gray-700">
                {analise.esperandoCliente.slice(0, LISTA).map(e => (
                  <li key={e.negociacao.id}>
                    <button type="button" onClick={() => abrir(e.negociacao.id)}
                      className="w-full flex items-start gap-3 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-lg px-2 -mx-2">
                      <span className="flex-1 min-w-0">
                        <span className="block text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{e.negociacao.cliente_nome || 'Sem nome'}</span>
                        <span className="block text-xs text-gray-400">
                          {e.motivo === 'documento' ? 'pendência documental' : 'garantia pedida'} em {dataCurta(e.desde)}
                          {kwh(e.negociacao.consumo_medio_kwh) ? ` · ${kwh(e.negociacao.consumo_medio_kwh)}` : ''}
                        </span>
                      </span>
                      <span className={`shrink-0 text-right text-xs font-semibold ${(e.dias ?? 0) > 30 ? 'text-red-600 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>
                        há {plural(e.dias ?? 0, 'dia', 'dias')}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-gray-400 mt-3">
                Cobre o cliente (ou o vendedor) pelo documento ou pela garantia. Quando chega, a negociação volta para a fila como reanálise, sem prazo. Estas não contam como análises a fazer.
                {analise.esperandoCliente.length > LISTA && ` Mais ${plural(analise.esperandoCliente.length - LISTA, 'análise', 'análises')}.`}
              </p>
            </>
          )}
        </PainelDetalhe>
      </div>

      <PainelReguaDaCarteira regua={regua} hoje={hoje} />

      <SecaoVisaoGeral titulo="Meu mês" subtitulo={`${rotuloMes}, análises decididas por ${(pessoa.full_name || '').split(' ')[0] || 'você'}`} colunas={3}>
        <CartaoIndicador
          titulo="Análises no prazo"
          valor={doMes ? (doMes.taxa == null ? '—' : pct(doMes.taxa)) : null}
          meta={doMes?.taxa != null ? { texto: `meta ${pct(META_ANALISES_NO_PRAZO)}`, atingido: doMes.taxa / META_ANALISES_NO_PRAZO } : undefined}
          detalhe={doMes ? `${doMes.noPrazo} de ${doMes.noPrazo + doMes.foraDoPrazo} primeiras análises decididas em até 2 dias úteis.${doMes.reanalises ? ` ${plural(doMes.reanalises, 'reanálise fica', 'reanálises ficam')} de fora: o prazo vale só para a primeira análise.` : ''}${doMes.semComoMedir ? ` ${plural(doMes.semComoMedir, 'análise sem data de assinatura fica', 'análises sem data de assinatura ficam')} de fora.` : ''}` : undefined}
          para="/gestao"
          origem="Da assinatura do termo até a primeira decisão registrada na Validação de Dados, inclusive 'Pendência Documental'. Depois da primeira decisão o prazo não conta mais: nem a espera pelo documento nem a reanálise."
        />
        <CartaoIndicador
          titulo="Análises do mês"
          valor={doMes ? plural(doMes.aprovadas + doMes.reprovadas, 'decidida', 'decididas') : null}
          composicao={doMes ? [
            { rotulo: 'Aprovadas', valor: String(doMes.aprovadas) },
            { rotulo: 'Reprovadas', valor: String(doMes.reprovadas), destaque: doMes.reprovadas > 0 },
            { rotulo: 'Pendência documental', valor: String(doMes.pendencia) },
            { rotulo: 'Garantia pedida', valor: String(doMes.garantia) },
          ] : undefined}
          detalhe={doMes ? `Pela última decisão de cada negociação no mês (${plural(doMes.decisoes, 'decisão registrada', 'decisões registradas')}).` : undefined}
          para="/gestao"
        />
        <CartaoIndicador
          titulo="Inadimplência da empresa"
          valor={inadimplencia ? (inadimplencia.taxa == null ? '—' : pct(inadimplencia.taxa)) : null}
          meta={metaInad}
          detalhe={inadimplencia ? `Boletos vencidos de ${dataCurta(inadimplencia.janela.inicio)} a ${dataCurta(inadimplencia.janela.fim)} ainda em aberto: ${reais(inadimplencia.vencido)}. Estoque vencido: ${reais(inadimplencia.estoque)}.` : undefined}
          origem="Regra do plano de bônus: vencido há mais de 30 dias e em aberto ÷ valor desses boletos, nos 3 meses anteriores. É do time inteiro."
        />
        <CartoesReguaEAcordos pessoaId={pessoa.id} inicioIso={primeiroDia} fimIso={ultimoDia} hojeIso={hoje} rotuloMes={rotuloMes} comRegua={false} />
      </SecaoVisaoGeral>

      <section className="space-y-3" aria-label="Detalhe">
        <h2 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">Detalhe</h2>
        <PainelDetalhe titulo="Por que as análises reprovam">
          {reprovacoes.erro ? (
            <p className="text-sm text-red-600 dark:text-red-400">Não foi possível carregar os motivos.</p>
          ) : (
            <ListaMotivos
              grupo={reprovacoes.dados}
              rotulo={codigo => rotuloMotivoReprovacaoAnalise(codigo, listaMotivos) ?? codigo}
              vazio={`Nenhuma reprovação na validação de dados em ${rotuloMes}.`}
            />
          )}
          <p className="text-xs text-gray-400 mt-3">
            Motivo principal escolhido na reprovação, de todas as análises do mês. Uma negociação reanalisada conta uma vez, pela última decisão.
          </p>
        </PainelDetalhe>
      </section>

      <ListaAtalhos itens={[
        { rotulo: 'Validação de dados', para: '/gestao', icone: ClipboardCheck },
        { rotulo: 'Lembretes de cobrança', para: '/financeiro/lembretes-cobranca', icone: Send },
        { rotulo: 'Esteira de cobrança', para: '/financeiro/esteira', icone: Workflow },
        { rotulo: 'Indicações', para: '/crm/indicacoes', icone: Gift },
      ]} />
    </div>
  );
};

export default TelaPosVenda;
